import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Typography,
  Container,
  CircularProgress,
  Box,
  Button,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Skeleton,
} from '@mui/material';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import VerifiedIcon from '@mui/icons-material/Verified';
import SearchOff from '@mui/icons-material/SearchOff';
import SmartWizard, { scoreCard } from './SmartWizard';
import WizardResults from './WizardResults';
import QuickFilterBar from './QuickFilterBar';
import CardRanked from './CardRanked';
import CompareTray from './CompareTray';
import ComparisonModal from './ComparisonModal';
import CardDetailModal from './CardDetailModal';
import { parseFee, parseRate, getRewardType, parseIncome } from './helpers';
import { adaptDataset } from './cardAdapter';

const CARDS_PER_PAGE = 12;

const INCOME_MIDPOINTS = {
  under_30k: 20000,
  '30k_60k': 45000,
  '60k_100k': 80000,
  '100k_plus': 130000,
};

const CreditCardComparisonTool = () => {
  const { t } = useTranslation();
  const [creditCards, setCreditCards] = useState([]);
  const [disclosure, setDisclosure] = useState({});
  const [selectedFilters, setSelectedFilters] = useState(null);
  const [sortOption, setSortOption] = useState('newcomer_first');
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Wizard state
  const [showWizard, setShowWizard] = useState(true);
  const [wizardCompleted, setWizardCompleted] = useState(false);
  const [wizardAnswers, setWizardAnswers] = useState(null);
  const [topRecommendations, setTopRecommendations] = useState([]);
  const [showFullGrid, setShowFullGrid] = useState(false);

  // Comparison state
  const [selectedForCompare, setSelectedForCompare] = useState([]);
  const [showComparison, setShowComparison] = useState(false);

  // Detail modal state
  const [detailCard, setDetailCard] = useState(null);

  const gridRef = useRef(null);
  const resultsRef = useRef(null);

  /* ── load data (v2 with fallback to v1) ─────── */
  useEffect(() => {
    // Try v2 first, fallback to v1
    fetch('/cards_v2.json')
      .then((res) => {
        if (!res.ok) throw new Error('v2 not found');
        return res.json();
      })
      .then((v2Data) => {
        const { cards, disclosure: disc } = adaptDataset(v2Data);
        setCreditCards(cards);
        setDisclosure(disc);
        setSelectedFilters({
          newcomer_friendly: false,
          no_annual_fee: false,
          bank: [],
          purchase_rate: [0, 30],
          reward_type: [],
        });
        setIsLoading(false);
      })
      .catch(() => {
        // Fallback: load v1
        fetch('/credit_card_data.json')
          .then((res) => res.json())
          .then((data) => {
            setCreditCards(data);
            setSelectedFilters({
              newcomer_friendly: false,
              no_annual_fee: false,
              bank: [],
              purchase_rate: [0, 30],
              reward_type: [],
            });
            setIsLoading(false);
          })
          .catch((err) => {
            console.error('Error cargando tarjetas:', err);
            setLoadError(true);
            setIsLoading(false);
          });
      });
  }, []);

  /* ── wizard complete ────────────────────────── */
  const handleWizardComplete = useCallback(
    (answers) => {
      setWizardAnswers(answers);
      setShowWizard(false);
      setWizardCompleted(true);

      const newFilters = {
        newcomer_friendly: false,
        no_annual_fee: false,
        bank: [],
        purchase_rate: [0, 30],
        reward_type: [],
      };

      if (answers.time_in_canada === 'new' && answers.credit_history === 'none') {
        newFilters.newcomer_friendly = true;
      }
      if (answers.priority === 'no_fee') {
        newFilters.no_annual_fee = true;
      }
      if (answers.priority === 'cashback') {
        newFilters.reward_type = ['cashback'];
      }

      setSelectedFilters(newFilters);

      // Score and find top 3
      let eligible = [...creditCards];
      if (newFilters.newcomer_friendly) {
        eligible = eligible.filter((c) => c.newcomer_friendly === true);
      }
      if (newFilters.no_annual_fee) {
        eligible = eligible.filter((c) => parseFee(c.annual_fee) === 0);
      }

      if (answers.income_range) {
        const midpoint = INCOME_MIDPOINTS[answers.income_range] || 45000;
        eligible = eligible.filter((c) => {
          const req = parseIncome(c.min_income_personal);
          return req === 0 || req <= midpoint;
        });
      }

      const scored = eligible
        .map((card) => ({ card, score: scoreCard(card, answers) }))
        .sort((a, b) => b.score - a.score);

      const top3 = scored.slice(0, 3).map((s) => s.card);
      setTopRecommendations(top3);
      setSortOption('recommended');
      setShowFullGrid(false);

      // Scroll to results after render
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    },
    [creditCards]
  );

  const handleWizardSkip = () => {
    setShowWizard(false);
    setWizardCompleted(false);
    setWizardAnswers(null);
    setTopRecommendations([]);
    setShowFullGrid(true);
  };

  const handleViewAll = () => {
    setShowFullGrid(true);
    setTimeout(() => {
      gridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleRepeatWizard = () => {
    setShowWizard(true);
    setWizardCompleted(false);
    setWizardAnswers(null);
    setTopRecommendations([]);
    setShowFullGrid(false);
  };

  /* ── V2 score sort ──────────────────────────── */
  const getV2Score = useCallback((card) => {
    return card.v2?.score?.finmaple_score || 0;
  }, []);

  /* ── bonus value sort ───────────────────────── */
  const getBonusValue = useCallback((card) => {
    return card.v2?.welcome_bonus?.value_estimate_cad?.high || 0;
  }, []);

  /* ── filter & sort (useMemo eliminates double-render cycle) ── */
  const filteredAndSortedCards = useMemo(() => {
    if (!selectedFilters || creditCards.length === 0) return [];

    let cards = [...creditCards];

    if (selectedFilters.newcomer_friendly) {
      cards = cards.filter((c) => c.newcomer_friendly === true);
    }
    if (selectedFilters.no_annual_fee) {
      cards = cards.filter((c) => parseFee(c.annual_fee) === 0);
    }
    if (selectedFilters.bank.length > 0) {
      cards = cards.filter((c) => selectedFilters.bank.includes(c.bank));
    }
    const [minRate, maxRate] = selectedFilters.purchase_rate || [0, 30];
    cards = cards.filter((c) => {
      const val = parseRate(c.purchase_rate);
      if (val === null) return true;
      return val >= minRate && val <= maxRate;
    });
    if (selectedFilters.reward_type.length > 0) {
      cards = cards.filter((c) => selectedFilters.reward_type.includes(getRewardType(c)));
    }

    // Pre-compute scores once (avoids calling scoreCard inside sort comparator)
    const recommendedSet = new Set(topRecommendations.map((c) => c.id));
    let scoreMap;
    if (sortOption === 'recommended' && wizardAnswers) {
      scoreMap = new Map();
      cards.forEach((c) => scoreMap.set(c.id, scoreCard(c, wizardAnswers)));
    }

    // Sort
    cards.sort((a, b) => {
      switch (sortOption) {
        case 'recommended': {
          const recA = recommendedSet.has(a.id) ? 0 : 1;
          const recB = recommendedSet.has(b.id) ? 0 : 1;
          if (recA !== recB) return recA - recB;
          if (scoreMap) return scoreMap.get(b.id) - scoreMap.get(a.id);
          return 0;
        }
        case 'no_fee_first':
          return parseFee(a.annual_fee) - parseFee(b.annual_fee);
        case 'lowest_rate':
          return (parseRate(a.purchase_rate) ?? 99) - (parseRate(b.purchase_rate) ?? 99);
        case 'newcomer_first': {
          const nA = a.newcomer_friendly ? 0 : 1;
          const nB = b.newcomer_friendly ? 0 : 1;
          if (nA !== nB) return nA - nB;
          return parseFee(a.annual_fee) - parseFee(b.annual_fee);
        }
        case 'finmaple_score':
          return getV2Score(b) - getV2Score(a);
        case 'highest_bonus':
          return getBonusValue(b) - getBonusValue(a);
        default:
          return 0;
      }
    });

    return cards;
  }, [selectedFilters, sortOption, creditCards, topRecommendations, wizardAnswers, getV2Score, getBonusValue]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedFilters, sortOption]);

  /* ── handlers ───────────────────────────────── */
  const handleFilterChange = (filterCategory, value) => {
    setSelectedFilters((prev) => ({ ...prev, [filterCategory]: value }));
  };

  const handleClearFilters = () => {
    setSelectedFilters({
      newcomer_friendly: false,
      no_annual_fee: false,
      bank: [],
      purchase_rate: [0, 30],
      reward_type: [],
    });
    setSortOption(wizardAnswers ? 'recommended' : 'newcomer_first');
  };

  const toggleCompare = (card) => {
    setSelectedForCompare((prev) => {
      const exists = prev.find((c) => c.id === card.id);
      if (exists) return prev.filter((c) => c.id !== card.id);
      if (prev.length >= 4) return prev;
      return [...prev, card];
    });
  };

  const handleOpenDetail = (card) => {
    setDetailCard(card);
  };

  /* ── pagination ─────────────────────────────── */
  const totalPages = Math.ceil(filteredAndSortedCards.length / CARDS_PER_PAGE);
  const paginatedCards = useMemo(() => {
    const start = (currentPage - 1) * CARDS_PER_PAGE;
    return filteredAndSortedCards.slice(start, start + CARDS_PER_PAGE);
  }, [filteredAndSortedCards, currentPage]);

  /* ── loading skeletons ─────────────────────── */
  if (isLoading || selectedFilters === null) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Skeleton variant="rounded" width={200} height={32} sx={{ mb: 2, borderRadius: '8px' }} />
        <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" width={90} height={32} sx={{ borderRadius: '999px' }} />
          ))}
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={140} sx={{ borderRadius: '14px' }} />
          ))}
        </Box>
      </Container>
    );
  }

  /* ── error state ───────────────────────────── */
  if (loadError) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: 'center' }}>
        <SearchOff sx={{ fontSize: 56, color: '#E6E9EE', mb: 2 }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 1 }}>
          {t('creditCards.errorTitle')}
        </Typography>
        <Typography variant="body2" sx={{ color: '#5F6B7A', mb: 3 }}>
          {t('creditCards.errorDescription')}
        </Typography>
        <Button
          variant="outlined"
          onClick={() => window.location.reload()}
          sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 600 }}
        >
          {t('creditCards.retry')}
        </Button>
      </Container>
    );
  }

  /* ── Product schema ─────────────────────────── */
  const productSchema = paginatedCards.slice(0, 6).map((card) => {
    return {
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: card.name,
      brand: { '@type': 'Brand', name: card.bank },
      description: card.v2?.description || `${card.name} credit card by ${card.bank}`,
    };
  });

  /* ── render ─────────────────────────────────── */
  return (
    <Container maxWidth="lg" sx={{ pb: 4 }}>
      {productSchema.length > 0 && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        </Helmet>
      )}

      {/* Step 1: Smart Wizard */}
      {showWizard && (
        <SmartWizard onComplete={handleWizardComplete} onSkip={handleWizardSkip} />
      )}

      {/* Step 2: Wizard Results - top 3 */}
      {wizardCompleted && !showWizard && topRecommendations.length > 0 && (
        <Box ref={resultsRef}>
          <WizardResults
            topCards={topRecommendations}
            onViewAll={handleViewAll}
            onRepeatWizard={handleRepeatWizard}
            wizardAnswers={wizardAnswers}
          />
        </Box>
      )}

      {/* Step 3: Full grid */}
      {!showWizard && (showFullGrid || !wizardCompleted) && (
        <Box ref={gridRef}>
          {/* Header row */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', md: 'center' },
              mb: 2,
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 1 }}>
              <Box
                key={filteredAndSortedCards.length}
                sx={{
                  '@keyframes fadeIn': {
                    from: { opacity: 0.4 },
                    to: { opacity: 1 },
                  },
                  animation: 'fadeIn 0.3s ease-out',
                }}
              >
                <Typography variant="h4" sx={{ color: 'text.primary' }}>
                  {t('creditCards.cardsFound', { count: filteredAndSortedCards.length })}
                </Typography>
              </Box>
              <Typography
                variant="caption"
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}
              >
                <VerifiedIcon sx={{ fontSize: '0.85rem', color: 'primary.main' }} />
                {t('creditCards.dataUpdated')}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 230 }}>
                <InputLabel id="sort-label">{t('creditCards.sortBy')}</InputLabel>
                <Select
                  labelId="sort-label"
                  value={sortOption}
                  label={t('creditCards.sortBy')}
                  onChange={(e) => setSortOption(e.target.value)}
                >
                  {wizardAnswers && <MenuItem value="recommended">{t('creditCards.sortRecommended')}</MenuItem>}
                  <MenuItem value="finmaple_score">{t('creditCards.sortFinmapleScore')}</MenuItem>
                  <MenuItem value="newcomer_first">{t('creditCards.sortNewcomerFirst')}</MenuItem>
                  <MenuItem value="no_fee_first">{t('creditCards.sortNoFeeFirst')}</MenuItem>
                  <MenuItem value="lowest_rate">{t('creditCards.sortLowestRate')}</MenuItem>
                  <MenuItem value="highest_bonus">{t('creditCards.sortHighestBonus')}</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="text"
                size="small"
                onClick={handleClearFilters}
                sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 600, '&:hover': { color: '#D32F2F' } }}
              >
                {t('creditCards.reset')}
              </Button>
            </Box>
          </Box>

          {/* Quick filter bar */}
          <QuickFilterBar
            selectedFilters={selectedFilters}
            onFilterChange={handleFilterChange}
            onClearFilters={handleClearFilters}
            allCards={creditCards}
          />

          {/* Ranked card list or empty state */}
          {filteredAndSortedCards.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, bgcolor: '#F7F9FC', borderRadius: '16px', mt: 2 }}>
              <SearchOff sx={{ fontSize: 56, color: '#E6E9EE', mb: 2 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1A1A1A', mb: 1 }}>
                {t('creditCards.emptyTitle')}
              </Typography>
              <Typography variant="body2" sx={{ color: '#5F6B7A', mb: 3, maxWidth: 400, mx: 'auto' }}>
                {t('creditCards.emptyDescription')}
              </Typography>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                sx={{ textTransform: 'none', borderRadius: '10px', fontWeight: 600, borderColor: '#1E3A5F', color: '#1E3A5F' }}
              >
                {t('creditCards.resetFilters')}
              </Button>
            </Box>
          ) : (
            <CardRanked
              cards={paginatedCards}
              selectedForCompare={selectedForCompare}
              onToggleCompare={toggleCompare}
              onViewDetail={handleOpenDetail}
              disclosure={disclosure}
              pageOffset={(currentPage - 1) * CARDS_PER_PAGE}
            />
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4, gap: 1.5 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                {t('creditCards.pageOf', { current: currentPage, total: totalPages })}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  disabled={currentPage === 1}
                  onClick={() => { setCurrentPage((p) => p - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  sx={{
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    fontWeight: 600,
                    px: { xs: 2, md: 3 },
                    py: { xs: 1.5, md: 1 },
                    minHeight: 44,
                    borderRadius: '10px',
                    textTransform: 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: 'rgba(30,58,95,0.04)', transform: 'translateY(-1px)' },
                    '&.Mui-disabled': { borderColor: 'divider', color: 'text.disabled' },
                  }}
                >
                  {t('creditCards.previous')}
                </Button>
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={(_, page) => { setCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  siblingCount={1}
                  boundaryCount={1}
                  sx={{
                    '& .MuiPaginationItem-root': {
                      color: 'text.primary',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                      minWidth: { xs: 44, md: 40 },
                      height: { xs: 44, md: 40 },
                      borderRadius: '10px',
                      transition: 'all 0.2s ease',
                      '&:hover': { bgcolor: 'rgba(30,58,95,0.06)' },
                    },
                    '& .Mui-selected': {
                      bgcolor: 'primary.main',
                      color: '#fff',
                      boxShadow: '0 2px 8px rgba(30,58,95,0.2)',
                      '&:hover': { bgcolor: 'primary.dark' },
                    },
                  }}
                />
                <Button
                  variant="outlined"
                  disabled={currentPage === totalPages}
                  onClick={() => { setCurrentPage((p) => p + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                  sx={{
                    borderColor: 'primary.main',
                    color: 'primary.main',
                    fontWeight: 600,
                    px: { xs: 2, md: 3 },
                    py: { xs: 1.5, md: 1 },
                    minHeight: 44,
                    borderRadius: '10px',
                    textTransform: 'none',
                    transition: 'all 0.2s ease',
                    '&:hover': { bgcolor: 'rgba(30,58,95,0.04)', transform: 'translateY(-1px)' },
                    '&.Mui-disabled': { borderColor: 'divider', color: 'text.disabled' },
                  }}
                >
                  {t('creditCards.next')}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Compare tray */}
      <CompareTray
        selectedCards={selectedForCompare}
        onRemove={toggleCompare}
        onClear={() => setSelectedForCompare([])}
        onCompare={() => setShowComparison(true)}
      />

      {/* Comparison Modal */}
      <ComparisonModal
        open={showComparison}
        onClose={() => setShowComparison(false)}
        cards={selectedForCompare}
        onRemoveCard={toggleCompare}
      />

      {/* Card Detail Modal */}
      <CardDetailModal
        open={!!detailCard}
        onClose={() => setDetailCard(null)}
        card={detailCard}
        selectedForCompare={selectedForCompare}
        onToggleCompare={toggleCompare}
        disclosure={disclosure}
      />
    </Container>
  );
};

export default CreditCardComparisonTool;
